<template>
  <VCard flat>
    <div class="flex items-center gap-2 mb-5 bg-gray-100 border border-gray-300 rounded pl-2">
      <SfIconSearch />
      <v-text-field
        v-model="search"
        density="compact"
        label="Search file or path..."
        variant="solo"
        flat
        hide-details
        single-line
      />
    </div>

    <div v-if="loading" class="flex justify-center items-center min-h-[300px]">
      <SfLoaderCircular size="2xl" class="text-gray-400" />
    </div>
    <v-data-table
      v-else
      :items="filteredItems"
      :headers="headers"
      class="border border-gray-300 rounded-md"
      no-data-text="No images or folders found"
    >
      <template #item="{ item }">
        <UiImageTableSkeleton v-if="item.storageClass === UPLOADING_CLASS" />
        <tr v-else :class="item.key === props.selectedKey ? 'bg-[#EFF4F1]' : ''">
          <td>
            <div class="flex flex-col gap-1 cursor-pointer" @click="onRowClick(item)">
              <div class="flex items-center gap-2">
                <NuxtImg
                  data-testid="image-table-thumbnail"
                  :src="item.previewUrl || item.publicUrl"
                  alt="table thumbnail"
                  class="w-8 h-8 rounded object-cover"
                />
                <span data-testid="image-table-file-name">{{ item.fileName }}</span>
              </div>
            </div>
          </td>
          <td data-testid="image-table-path">{{ item.path }}</td>
          <td data-testid="image-table-size">{{ bytesToMB(item.size) }}</td>
          <td data-testid="image-table-last-modified">{{ formatDate(item.lastModified) }}</td>
        </tr>
      </template>
    </v-data-table>
  </VCard>
</template>

<script setup lang="ts">
import { VCard, VTextField, VDataTable } from 'vuetify/components';
import type { StorageObject } from '@plentymarkets/shop-api';
import { SfLoaderCircular, SfIconSearch } from '@storefront-ui/vue';

const { data: items, loading, headers, bytesToMB, formatDate, getStorageMetadata } = useItemsTable();

const { setMetadata } = useImageMetadata();
const lastFetchedKey = ref<string | null>(null);

const props = defineProps<{
  selectedKey: string | null;
}>();
watch(
  () => props.selectedKey,
  (key) => {
    if (!key || key === lastFetchedKey.value) return;

    const row = items.value.find((item) => item.key === key);
    if (!row || row.storageClass === UPLOADING_CLASS) return;

    lastFetchedKey.value = key;

    emit('select', { name: row.key, image: row.publicUrl });
    fetchMetadata(key);
  },
  { immediate: false },
);

const search = ref('');

const itemsWithPath = computed(() =>
  items.value
    .filter((item: StorageObject) => !item.key.endsWith('/'))
    .map((item: StorageObject) => {
      const lastSlash = item.key.lastIndexOf('/');
      return {
        ...item,
        fileName: lastSlash >= 0 ? item.key.slice(lastSlash + 1) : item.key,
        path: lastSlash >= 0 ? item.key.slice(0, lastSlash + 1) : '',
      };
    }),
);

const filteredItems = computed(() => {
  if (!search.value) return itemsWithPath.value;
  const s = search.value.toLowerCase();
  return itemsWithPath.value.filter(
    (item) => item.fileName.toLowerCase().includes(s) || item.path.toLowerCase().includes(s),
  );
});
const onRowClick = (item: StorageObject) => {
  emit('update:selectedKey', item.key);
  handleRowClick(item);
  fetchMetadata(item.key);
};

const fetchMetadata = async (key: string) => {
  const data = await getStorageMetadata(key);
  if (data && data.width && data.height) {
    setMetadata(key, { width: data.width, height: data.height });
  }
};

const emit = defineEmits<{
  (e: 'update:selectedKey', value: string | null): void;
  (e: 'select', item: { name: string; image: string }): void;
  (e: 'unselect'): void;
}>();

const handleRowClick = (item: StorageObject) => {
  emit('select', {
    name: item.key,
    image: item.publicUrl,
  });
};
</script>

<style>
.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > td,
.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > th {
  border-bottom: none !important;
}
.v-data-table-footer__items-per-page {
  display: none !important;
}

.v-data-table-footer__info {
  display: none !important;
}

.v-ripple__container {
  display: none !important;
}

div.v-data-table-footer > div.v-data-table-footer__pagination > nav > ul > li.v-pagination__next > button:active {
  background-color: gray;
}

div.v-data-table-footer > div.v-data-table-footer__pagination > nav > ul > li.v-pagination__prev > button:active {
  background-color: gray;
}
</style>
